import { Inject, Injectable } from '@angular/core';
import {Subject} from 'rxjs';
import { HttpSearchResultTag, HTTPSort, State, StateFilters } from '../models';
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../config/hotel-tours-list-page.config";

@Injectable({providedIn: 'root'})
export class ToursStateService {
  private readonly defaultState: State = {
    page: 1,
    groupMode: 1,
    size: 20,
    sort: this.hotelToursPageConfig.searchDefaultSortVariant,
    selectedFiltersCount: 0,
    applyedFiltersCount: 0,
    selectedFilters: {
      airlines: [],
      beachTypes: [],
      datesList: [],
      hotelServices: [],
      hotelsIds: [],
      mealIds: [],
      nightsList: [],
      ratings: [],
      regionsIds: [],
      seaLines: [],
      stars: [],
      smartfilters: null,
      priceMax: 0,
      priceMin: 0,
      onlyRecommendedHotels: false,
      onlySmart: false,
    },
    applyedFilters: {
      airlines: [],
      beachTypes: [],
      datesList: [],
      hotelServices: [],
      hotelsIds: [],
      mealIds: [],
      nightsList: [],
      ratings: [],
      regionsIds: [],
      seaLines: [],
      stars: [],
      smartfilters: null,
      priceMax: 0,
      priceMin: 0,
      onlyRecommendedHotels: false,
      onlySmart: false,
    }
  };

  private currentState: State;

  private updatedSub = new Subject<State>();
  updated$ = this.updatedSub.asObservable();

  private tags: HttpSearchResultTag[] = null;

  private clearSub = new Subject<State>();
  clear$ = this.clearSub.asObservable();

  private filtersApplyedSub = new Subject<void>();
  filtersApplyed$ = this.filtersApplyedSub.asObservable();

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
  ) {
    this.currentState = JSON.parse(JSON.stringify(this.defaultState));
  }

  private stateUpdated(): void {
    this.updatedSub.next(this.currentState);
  }

  private convertFilterName(name: string): string {
    switch (name) {
      case 'beaches':
        return 'beachTypes';
      case 'meals':
        return 'mealIds';
      case 'nights':
        return 'nightsList';
      case 'regions':
        return 'regionsIds';
      default:
        return name;
    }
  }

  filterSelected(key: string, value: string | number, applied = false): boolean {
    const stateKey = this.convertFilterName(key);
    const filters = applied ? this.currentState.applyedFilters : this.currentState.selectedFilters;

    if (!filters.hasOwnProperty(stateKey)) {
      return false;
    }

    const filterState = filters[stateKey];

    if (Array.isArray(filterState)) {
      return filterState.indexOf(value) !== -1;
    }

    if (typeof filterState === 'boolean') {
      return filterState === Boolean(value);
    }

    if (typeof filterState === 'number') {
      return filterState === Number(value);
    }

    return false;
  }

  toggleFilterValue(key: string, value: string | number): void {
    const stateKey = this.convertFilterName(key);
    const filterState = this.currentState.selectedFilters[stateKey];

    if (Array.isArray(filterState)) {
      if (this.filterSelected(stateKey, value)) {
        const index = filterState.indexOf(value);
        filterState.splice(index, 1);
        this.currentState.selectedFiltersCount--;
      } else {
        filterState.push(value);
        this.currentState.selectedFiltersCount++;
      }
      return;
    }

    if (typeof filterState === 'boolean') {
      const nextValue = Boolean(value);

      if (filterState === nextValue) {
        if (nextValue) {
          this.currentState.selectedFilters[stateKey] = false;
          this.currentState.selectedFiltersCount = Math.max(0, this.currentState.selectedFiltersCount - 1);
        }

        return;
      }

      this.currentState.selectedFilters[stateKey] = nextValue;

      if (!filterState && nextValue) {
        this.currentState.selectedFiltersCount++;
      } else if (filterState && !nextValue) {
        this.currentState.selectedFiltersCount = Math.max(0, this.currentState.selectedFiltersCount - 1);
      }
      return;
    }

    if (typeof filterState === 'number') {
      this.currentState.selectedFilters[stateKey] = Number(value);
      this.updateSelectedFiltersCount();
    }
  }

  setSmartFilters(tagId: number) {
    const smartfilters = this.currentState.selectedFilters.smartfilters;

    if (!smartfilters) {
      this.currentState.selectedFilters.smartfilters = {
        bestHotelOnly: false,
        selectMode: 3,
        tagsIds: []
      };
      this.currentState.selectedFiltersCount++;
    }

    let tagsIds = this.currentState.selectedFilters.smartfilters.tagsIds;

    if (tagsIds.indexOf(tagId) === -1) {
      tagsIds.push(tagId); // Добавляем, если не найден
    } else {
      tagsIds.splice(tagsIds.indexOf(tagId), 1); // Удаляем, если найден
    }

    if (tagsIds.length > 0) {
      this.currentState.selectedFilters.smartfilters.tagsIds = tagsIds;
    } else {
      this.currentState.selectedFilters.smartfilters = null;
      this.currentState.selectedFiltersCount--;
    }
  }

  applyFilters(): void {
    this.currentState.applyedFilters = JSON.parse(JSON.stringify(this.currentState.selectedFilters));
    this.currentState.applyedFiltersCount = JSON.parse(JSON.stringify(this.currentState.selectedFiltersCount));
    this.currentState.page = 1;

    this.filtersApplyedSub.next();

    this.stateUpdated();
  }

  resetFilters(): void {
    this.currentState.applyedFilters = JSON.parse(JSON.stringify(this.defaultState.applyedFilters));
    this.currentState.selectedFilters = JSON.parse(JSON.stringify(this.defaultState.selectedFilters));
    this.currentState.sort = this.defaultState.sort;

    this.currentState.applyedFiltersCount = 0;
    this.currentState.selectedFiltersCount = 0;

    this.stateUpdated();
  }

  resetFilter(key: string): void {
    const stateKey = this.convertFilterName(key);

    if (!this.currentState.selectedFilters.hasOwnProperty(stateKey)) {
      return;
    }

    this.currentState.selectedFilters[stateKey] = JSON.parse(JSON.stringify(this.defaultState.selectedFilters[stateKey]));
    this.updateSelectedFiltersCount();

    this.stateUpdated();
  }

  hasSelectedFilter(key: string): boolean {
    const stateKey = this.convertFilterName(key);

    if (!this.currentState.selectedFilters.hasOwnProperty(stateKey)) {
      return false;
    }

    return this.countSelectedFilterValue(this.currentState.selectedFilters[stateKey]) > 0;
  }

  hasSelectedFilters(): boolean {
    return this.currentState.selectedFiltersCount > 0;
  }

  setPage(page: number): void {
    this.currentState.page = page;
  }

  setSort(sort: HTTPSort): void {
    this.currentState.sort = sort;
    this.stateUpdated();
  }

  applySmartFilters(sort: HTTPSort, filters: Partial<StateFilters>): void {
    this.currentState.sort = sort;
    const selectedFilters = this.currentState.selectedFilters as unknown as Record<string, unknown>;
    const applyedFilters = this.currentState.applyedFilters as unknown as Record<string, unknown>;

    Object.keys(filters).forEach((key: keyof StateFilters) => {
      if (!this.currentState.selectedFilters.hasOwnProperty(key)) {
        return;
      }

      selectedFilters[key] = JSON.parse(JSON.stringify(filters[key]));
      applyedFilters[key] = JSON.parse(JSON.stringify(filters[key]));
    });

    this.updateSelectedFiltersCount();
    this.updateApplyedFiltersCount();
    this.stateUpdated();
  }

  getState(): State {
    return this.currentState;
  }

  clearState(): void {
    this.currentState = JSON.parse(JSON.stringify(this.defaultState));
    this.clearSub.next(this.currentState);
  }

  setTags(tags) {
    this.tags = tags;
  }

  getTags() {
    return this.tags;
  }

  private updateSelectedFiltersCount(): void {
    this.currentState.selectedFiltersCount = this.countSelectedFilters(this.currentState.selectedFilters);
  }

  private updateApplyedFiltersCount(): void {
    this.currentState.applyedFiltersCount = this.countSelectedFilters(this.currentState.applyedFilters);
  }

  private countSelectedFilters(filters: StateFilters): number {
    return Object.keys(filters).reduce((count, key: keyof StateFilters) => {
      return count + this.countSelectedFilterValue(filters[key]);
    }, 0);
  }

  private countSelectedFilterValue(value: unknown): number {
    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    }

    if (typeof value === 'number') {
      return value > 0 ? 1 : 0;
    }

    return value ? 1 : 0;
  }
}
